import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import queryClient from '@utils/queries/QueryClient';
import axios from 'axios';

import { useUserDamageParameters, useUserInfiltrationParameters } from '@utils/queries/user.queries';
import { Button, Icon, Input } from '@nazka/nazka.mapframe.components'
import { DataGrid } from '@mui/x-data-grid';
import { toast } from 'react-toastify';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import {
  columns, defaultInfiltrationParameters, defaultRows, infiltrationColumn, 
} from './damage-parameter-table.constants';
import { setShowDamagePrameterTable, setSpecificDamageParameterId } from '../damage-parameter.redux.actions';

import './damage-parameter-table.styl'

function DamageParameterTable() {
  const dispatch = useDispatch()

  const { data: userDamageParameters = [] } = useUserDamageParameters()
  const { data: userInfiltrationParameters = [] } = useUserInfiltrationParameters()
  const userSelectedMode = useSelector(state => state.maplibreMap.userSelectedMode);
  
  const allParameter = userSelectedMode === 'River flood' ? userDamageParameters : userInfiltrationParameters
  const specificDamageParameterId = useSelector(state => state.damageParameter.specificDamageParameterId)
  const currentParameter = allParameter.find(damage => damage.id === specificDamageParameterId)

  const [rows, setRows] = useState([]);
  
  const [parameterName, setParameterName] = useState(currentParameter?.parameterName || null)
  const endpointURL = userSelectedMode === 'River flood' ? '/api/damage-parameters/' : '/api/infiltration-parameters/'

  const handleProcessRowUpdate = (newRow, oldRow) => {
    const changedAttributes = Object.keys(newRow).filter(key => newRow[key] !== oldRow[key]);

    if (newRow[changedAttributes[0]] < 0) {
      toast.error('Value cannot be negative number')
      return oldRow
    }

    if (userSelectedMode === 'Flash flood' && newRow[changedAttributes[0]] > 100) {
      toast.error('Value cannot be more than 100%')
      return oldRow
    }

    const updatedRows = rows.map(row => (row.id === newRow.id ? newRow : row));

    setRows(updatedRows)
    
    return newRow; 
  };

  const { mutate } = useMutation({
    mutationFn: async () => {
      const payload = {
        parameterName: parameterName ?? 'New parameter set',
        landCoverClasses: rows,
      };
      const { data } = await axios.post(endpointURL, payload);
      return data;
    },
    onSuccess: () => {
      toast.success('Successfully created your custom damage parameter')
      dispatch(setShowDamagePrameterTable(false))
      dispatch(setSpecificDamageParameterId(null))
    },
    onSettled: () => {
      const queryKey = userSelectedMode === 'River flood' ? 'damage-parameters' : 'infiltration-parameters'
      queryClient.invalidateQueries({ queryKey: ['user', queryKey] })
    },
  })

  const { mutate: updateUserDamageParameter } = useMutation({
    mutationFn: async (damageParameterId) => {
      await axios.put(`${endpointURL}${damageParameterId}`, {
        parameterName,
        landCoverClasses: rows,
      });
    },
    onSettled: () => {
      const queryKey = userSelectedMode === 'River flood' ? 'damage-parameters' : 'infiltration-parameters'
      queryClient.invalidateQueries({ queryKey: ['user', queryKey] })
    },
    onSuccess: () => {
      toast.success('Successfully updated your damage parameter')
      dispatch(setShowDamagePrameterTable(false))
      dispatch(setSpecificDamageParameterId(null))
    },
    onError: () => toast.error('Something went wrong updating your area name'),
  });

  useEffect(() => {
    setRows(userSelectedMode === 'River flood'
      ? (currentParameter?.landCoverClasses || defaultRows)
      : (currentParameter?.landCoverClasses || defaultInfiltrationParameters));
  }, [userSelectedMode, currentParameter, specificDamageParameterId]);

  return (
    <div className="damage-parameter-table">
      <div className="damage-parameter-table__wrapper">
        <h3>{userSelectedMode === 'River flood' ? 'Damage parameters' : 'Infiltration parameters'}</h3>
        <span>
          Parameter name:
        </span>
        <div>
          <Input
            onInputChange={(e) => {
              setParameterName(e.target.value)
            }} 
            rootElProps={{ placeholder: 'New parameter set' }}
            css={{
              border: '1px solid #ddd',
              borderRadius: '4px',
              outline: 'none',
              height: '25px',
              width: '200px',
            }}
            value={parameterName}
          />
        </div>
       
        <div className="damage-parameter-table__data-grid">
          <DataGrid
            hideFooterPagination
            columns={userSelectedMode === 'River flood' ? columns : infiltrationColumn}
            rows={rows}
            rowHeight={25}
            sx={{
              width: userSelectedMode === 'River flood' ? '100%' : '50%',
              '& .MuiDataGrid-cell': {
                borderRight: '1px solid #e0e0e0',
              },
              '& .MuiDataGrid-root': {
                width: '100%',
              },
              '& .MuiDataGrid-columnHeader': {
                borderRight: '1px solid #e0e0e0',
                backgroundColor: '#f5f5f5',
              },
            }}
            processRowUpdate={handleProcessRowUpdate}
            experimentalFeatures={{ newEditingApi: true }}
            disableColumnVirtualization
            disableVirtualization
            disableColumnResize
          />
        </div>
        <div className="damage-parameter-table__cta__wrapper">
          {userSelectedMode === 'River flood' && (
          <div className="damage-parameter-table__info">
            <Icon icon={faInfoCircle} size="24px" />
            <span>All values are in EUR/sqm</span>
            <span>* K(H) and O(H) is a factor that takes flood height into consideration when performing damage calculations</span>
          </div>
          )}
          
          <div className="damage-parameter__confirmation__wrapper">
            <Button
              color="#0367A5"
              hoverColor="#0367A5"
              layout="outlined"
              css={{
                borderRadius: '3px',
              }}
              onClick={() => {
                dispatch(setSpecificDamageParameterId(null))
                dispatch(setShowDamagePrameterTable(false))
              }}
            >
              Cancel
            </Button>
            <Button
              color="#0367A5"
              hoverColor="#0367A5"
              css={{
                borderRadius: '3px',
              }}
              onClick={() => {
                if (specificDamageParameterId) {
                  updateUserDamageParameter(specificDamageParameterId)
                } else {
                  mutate()
                }
              }}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DamageParameterTable